import React, { useEffect } from "react";

import { isEqual } from "lodash";

import { Label } from "@busbud/horizon";
import { VehicleType } from "@busbud/int-schemas/lib/vehicles";

import { SelectableCardSkeleton } from "@app/components/selectable-card/skeleton";
import { SimplifiedTermsSection } from "@app/components/terms-and-conditions/components/terms-simplified-section";
import { useAppContext } from "@app/helpers/hooks";

import type { ConnectorProps } from "./fares-list.connect";

import { extractTerms } from "./extract-fare-terms";
import { FareCard } from "./fare-card";

const MAX_SUPPORTED_FARES = 4;

interface Props extends ConnectorProps {
  selectTripId: React.Dispatch<React.SetStateAction<string | undefined>>;
  selected_trip_id?: string;
  loading?: boolean;
}

const FaresListSkeleton = () => (
  <div className="flex flex-col items-center justify-center gap-300">
    <SelectableCardSkeleton selected />
    <SelectableCardSkeleton />
    <SelectableCardSkeleton />
  </div>
);

const FaresListComponent: React.FC<Props> = ({
  fares,
  selected_trip_id,
  selectTripId,
  loading,
  ticket_type,
  operator,
  trip_type,
  vehicle_type
}) => {
  const { termsBuilder, translator, sentry } = useAppContext();
  const { global_terms, fares_terms } = extractTerms(
    termsBuilder,
    ticket_type,
    operator,
    trip_type || "",
    vehicle_type || VehicleType.Bus,
    fares
  );

  const has_shared_terms = fares_terms.length > 1;

  useEffect(() => {
    if (fares && fares.length > MAX_SUPPORTED_FARES) {
      const error = new Error(
        `Too many fares available, only ${MAX_SUPPORTED_FARES} are supported. Fare count: ${fares.length}`
      );
      sentry?.captureException(error, {
        tags: {
          page: "results",
          component: "FaresList"
        },
        extra: { fares }
      });
    }
  }, [fares, sentry]);

  if (loading) {
    return <FaresListSkeleton />;
  }

  return (
    <div className="flex flex-col gap-300">
      <div className="flex flex-col gap-200">
        {fares_terms.map((fare, index) => {
          if (index < MAX_SUPPORTED_FARES) {
            const { trip_id, ...other_props } = fare;
            return (
              <FareCard
                key={fare.fare_xid}
                selected={selected_trip_id === trip_id}
                onSelect={() => selectTripId(trip_id)}
                // @ts-expect-error TS2532 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
                base_price_in_cents={fares_terms[0].price_in_cents}
                {...other_props}
              />
            );
          }
        })}
      </div>
      <div className="flex flex-col gap-200 px-100">
        {!!has_shared_terms && (
          <Label size="lg" fontWeight="bold" as="p">
            {translator.t("!results.classes-fares.general-terms")}
          </Label>
        )}
        {global_terms.map((terms, index) => (
          <SimplifiedTermsSection
            key={index}
            title={terms.title}
            terms={terms.items}
          />
        ))}
      </div>
    </div>
  );
};

export const FaresList = React.memo(FaresListComponent, isEqual);
